import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useStore } from '../store/useStore';
import {
  ContractWithDetails, ContractContent, ContractStatus, ContractActivity
} from '../types/contracts';
import { ContractEditor } from '../components/contracts/ContractEditor';
import { ContractViewer } from '../components/contracts/ContractViewer';
import { VersionHistory } from '../components/contracts/VersionHistory';
import { ContractCertificate } from '../components/contracts/ContractCertificate';
import { ContractTimeline } from '../components/contracts/ContractTimeline';
import { Modal } from '../components/Modal';
import {
  FileText, ArrowLeft, Send, Eye, Edit3, Clock,
  Copy, CheckCircle2, AlertCircle, XCircle, RefreshCw,
  FileSignature, Loader2, Activity, Award, Trash2, BookmarkPlus
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { saveCustomTemplate } from '../data/contractTemplates';

type TabType = 'editor' | 'preview' | 'signatures' | 'history' | 'certificate';

const STATUS_CONFIG: Record<ContractStatus, { label: string; color: string; icon: any }> = {
  draft:     { label: 'Draft',     color: 'bg-[var(--t-surface-hover)] text-[var(--t-text-muted)]', icon: FileText },
  sent:      { label: 'Sent',      color: 'bg-[var(--t-info-dim)] text-[var(--t-info)]',             icon: Send },
  viewed:    { label: 'Viewed',    color: 'bg-blue-500/10 text-blue-400',                             icon: Eye },
  countered: { label: 'Countered', color: 'bg-[var(--t-warning-dim)] text-[var(--t-warning)]',       icon: RefreshCw },
  accepted:  { label: 'Accepted',  color: 'bg-emerald-500/10 text-emerald-400',                       icon: CheckCircle2 },
  executed:  { label: 'Executed',  color: 'bg-[var(--t-success-dim)] text-[var(--t-success)]',        icon: FileSignature },
  rejected:  { label: 'Rejected',  color: 'bg-[var(--t-error)]/10 text-[var(--t-error)]',             icon: XCircle },
};

const ACTION_LABEL: Record<string, string> = {
  created: '📝 Contract created',
  sent: '📤 Sent for signature',
  viewed: '👁 Contract viewed by signer',
  countered: '🔄 Counter-offer submitted',
  accepted: '✅ Terms accepted',
  rejected: '❌ Contract rejected',
  executed: '🎉 Fully executed',
  signed: '✍️ Signed',
};

const DELETABLE_STATUSES: ContractStatus[] = ['draft', 'rejected'];

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useStore();

  const [dbContract, setContract] = useState<ContractWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('editor');

  // Send modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [signingLink, setSigningLink] = useState('');

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Save as template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('Custom');

  // Stage change confirm modal
  const [showStageConfirmModal, setShowStageConfirmModal] = useState(false);
  const [pendingStage, setPendingStage] = useState<ContractStatus | null>(null);

  useEffect(() => {
    if (id) fetchContract(id);
  }, [id]);

  const fetchContract = async (contractId: string) => {
    if (!isSupabaseConfigured || !supabase) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const { data: c, error: cErr } = await supabase
        .from('contracts')
        .select('*')
        .eq('id', contractId)
        .single();
      if (cErr) throw cErr;

      const { data: v, error: vErr } = await supabase
        .from('contract_versions')
        .select('*')
        .eq('contract_id', contractId)
        .order('version_number', { ascending: false });
      if (vErr) throw vErr;

      const { data: s, error: sErr } = await supabase
        .from('contract_signatures')
        .select('*')
        .eq('contract_id', contractId);
      if (sErr) throw sErr;

      setContract({
        ...c,
        versions: v || [],
        signatures: s || [],
        activity_log: c.activity_log || [],
      });
    } catch (err: any) {
      toast.error(`Error loading contract: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const logActivity = async (contractId: string, action: string, note?: string, currentLog: ContractActivity[] = []) => {
    const entry: ContractActivity = {
      id: uuidv4(),
      action,
      actor: currentUser?.name || 'Agent',
      timestamp: new Date().toISOString(),
      note,
    };
    const newLog = [...currentLog, entry];
    await supabase!.from('contracts').update({ activity_log: newLog }).eq('id', contractId);
    return newLog;
  };

  const handleSave = async (content: ContractContent, leadId: string | null, buyerLeadId?: string | null, sellerLeadId?: string | null) => {
    if (!currentUser || !supabase || !dbContract) return;
    setIsSaving(true);
    try {
      const isDraft = dbContract.status === 'draft';
      const latestVersionNumber = dbContract.versions.length > 0 ? dbContract.versions[0].version_number : 0;

      if (isDraft && dbContract.versions.length > 0) {
        const { error } = await supabase.from('contract_versions')
          .update({ content })
          .eq('id', dbContract.versions[0].id);
        if (error) throw error;

        if (leadId !== dbContract.lead_id || buyerLeadId !== dbContract.buyer_lead_id || sellerLeadId !== dbContract.seller_lead_id) {
          await supabase.from('contracts').update({ 
            lead_id: leadId,
            buyer_lead_id: buyerLeadId || null,
            seller_lead_id: sellerLeadId || null
          }).eq('id', dbContract.id);
        }
        toast.success('Draft updated');
      } else if (isDraft && dbContract.versions.length === 0) {
        const { error } = await supabase.from('contract_versions').insert({
          contract_id: dbContract.id,
          version_number: 1,
          content,
          change_summary: 'Initial draft',
          created_by: currentUser.id,
        });
        if (error) throw error;

        if (leadId !== dbContract.lead_id || buyerLeadId !== dbContract.buyer_lead_id || sellerLeadId !== dbContract.seller_lead_id) {
          await supabase.from('contracts').update({ 
            lead_id: leadId,
            buyer_lead_id: buyerLeadId || null,
            seller_lead_id: sellerLeadId || null
          }).eq('id', dbContract.id);
        }
        toast.success('Draft saved');
      } else {
        const { error } = await supabase.from('contract_versions').insert({
          contract_id: dbContract.id,
          version_number: latestVersionNumber + 1,
          content,
          change_summary: 'Agent updated terms',
          created_by: currentUser.id,
        });
        if (error) throw error;

        await logActivity(dbContract.id, 'countered', 'Agent submitted revised terms', dbContract.activity_log);
        await supabase.from('contracts').update({ status: 'countered' }).eq('id', dbContract.id);
        toast.success('New version created — status set to Countered');
      }

      fetchContract(dbContract.id);
    } catch (err: any) {
      toast.error(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendForSignature = async () => {
    if (!dbContract || !supabase) return;
    setIsSaving(true);
    try {
      const token = uuidv4().replace(/-/g, '') + uuidv4().replace(/-/g, '');
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 14);

      const newLog = await logActivity(dbContract.id, 'sent', 'Signing link generated', dbContract.activity_log);

      const { error } = await supabase.from('contracts').update({
        signing_token: token,
        expires_at: expiresAt.toISOString(),
        status: 'sent',
        activity_log: newLog,
      }).eq('id', dbContract.id);

      if (error) throw error;

      const link = `${window.location.origin}/sign/${token}`;
      setSigningLink(link);
      setShowSendModal(true);
      fetchContract(dbContract.id);
    } catch (err: any) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: ContractStatus) => {
    if (!dbContract || !supabase) return;
    setIsSaving(true);
    try {
      const newLog = await logActivity(dbContract.id, newStatus, undefined, dbContract.activity_log);
      const { error } = await supabase.from('contracts')
        .update({ status: newStatus, activity_log: newLog })
        .eq('id', dbContract.id);
      if (error) throw error;
      toast.success(`Contract marked as ${newStatus}`);
      fetchContract(dbContract.id);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!dbContract || !supabase) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase.from('contracts').delete().eq('id', dbContract.id);
      if (error) throw error;
      toast.success('Contract deleted');
      navigate('/contracts');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveAsTemplate = () => {
    if (!dbContract || !templateName.trim()) return;
    const content = dbContract.versions[0]?.content;
    if (!content) {
      toast.error('No content to save as template');
      return;
    }
    saveCustomTemplate({
      id: `custom-${uuidv4()}`,
      name: templateName.trim(),
      category: templateCategory || 'Custom',
      description: `Custom template from "${dbContract.title}"`,
      icon: '⭐',
      content,
    });
    toast.success(`Template "${templateName}" saved!`);
    setShowTemplateModal(false);
    setTemplateName('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(signingLink);
    toast.success('Link copied!');
  };

  if (!isLoading && !dbContract) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-[var(--t-text-muted)]">Contract not found.</p>
        <button onClick={() => navigate('/contracts')} className="text-[var(--t-primary)] font-semibold hover:underline">
          ← Back to Contracts
        </button>
      </div>
    );
  }

  const dummyContract: ContractWithDetails = {
    id: '',
    title: 'Loading Contract...',
    status: 'draft',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    lead_id: null,
    signing_token: null,
    expires_at: null,
    activity_log: [],
    signatures: [],
    versions: []
  };

  const contract = dbContract || dummyContract;

  const statusCfg = STATUS_CONFIG[contract.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;
  const latestContent = contract.versions[0]?.content;
  const isDraft = contract.status === 'draft';
  const isExecuted = contract.status === 'executed';
  const canDelete = DELETABLE_STATUSES.includes(contract.status);

  const tabs: Array<{ id: TabType; label: string; icon: any }> = [
    { id: 'editor', label: 'Editor', icon: Edit3 },
    { id: 'preview', label: 'Preview', icon: Eye },
    { id: 'signatures', label: `Signatures (${contract.signatures.length})`, icon: FileSignature },
    { id: 'history', label: 'History', icon: Clock },
    ...(isExecuted ? [{ id: 'certificate' as TabType, label: 'Certificate', icon: Award }] : []),
  ];

  return (
    <div className={`flex flex-col h-full bg-[var(--t-surface)] ${isLoading ? 'opacity-50 animate-pulse' : ''}`}>
      {/* Header */}
      <div className="px-4 py-1.5 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <button
          onClick={() => navigate('/contracts')}
          className="flex items-center gap-1.5 text-xs text-[var(--t-text-muted)] hover:text-[var(--t-text)] transition-colors mb-0.5"
        >
          <ArrowLeft size={13} /> Contracts
        </button>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[var(--t-primary-dim)] flex items-center justify-center shrink-0">
              <FileText className="text-[var(--t-primary)]" size={16} />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-[var(--t-text)] truncate">{contract.title}</h1>
              <div className="flex items-center gap-3">
                <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ${statusCfg.color}`}>
                  <StatusIcon size={10} /> {statusCfg.label}
                </span>
                {contract.versions.length > 0 && (
                  <span className="text-xs text-[var(--t-text-muted)]">v{contract.versions[0].version_number}</span>
                )}
                <span className="text-xs text-[var(--t-text-muted)]">
                  Updated {formatDistanceToNow(new Date(contract.updated_at), { addSuffix: true })}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {contract.status === 'accepted' && (
              <button
                onClick={() => handleStatusChange('executed')}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--t-success)] text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
              >
                <FileSignature size={15} /> Execute Contract
              </button>
            )}
            {(contract.status === 'sent' || contract.status === 'viewed' || contract.status === 'countered') && (
              <>
                <button
                  onClick={() => handleStatusChange('accepted')}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
                >
                  <CheckCircle2 size={15} /> Accept
                </button>
                <button
                  onClick={() => handleStatusChange('rejected')}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--t-error)]/10 text-[var(--t-error)] text-sm font-semibold rounded-xl hover:bg-[var(--t-error)]/20 transition-colors"
                >
                  <XCircle size={15} /> Reject
                </button>
              </>
            )}
            {isDraft && contract.versions.length > 0 && (
              <button
                onClick={handleSendForSignature}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
              >
                {isSaving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Send for Signature
              </button>
            )}
            {contract.signing_token && contract.status !== 'executed' && (
              <button
                onClick={() => { setSigningLink(`${window.location.origin}/sign/${contract.signing_token}`); setShowSendModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)] hover:border-[var(--t-primary)] transition-colors"
              >
                <Copy size={15} /> Copy Link
              </button>
            )}
            {/* Save as Template */}
            {latestContent && (
              <button
                onClick={() => { setTemplateName(contract.title); setShowTemplateModal(true); }}
                className="p-2 text-[var(--t-text-muted)] hover:text-[var(--t-primary)] hover:bg-[var(--t-primary-dim)] rounded-xl transition-colors"
                title="Save as template"
              >
                <BookmarkPlus size={17} />
              </button>
            )}
            {/* Delete — only for draft/rejected */}
            {canDelete ? (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 text-[var(--t-text-muted)] hover:text-[var(--t-error)] hover:bg-[var(--t-error)]/10 rounded-xl transition-colors"
                title="Delete contract"
              >
                <Trash2 size={17} />
              </button>
            ) : (
              <button
                disabled
                className="p-2 text-[var(--t-text-muted)] opacity-30 rounded-xl cursor-not-allowed"
                title="Cannot delete active contracts"
              >
                <Trash2 size={17} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-1 pt-1 border-t border-[var(--t-border)]">
          <ContractTimeline 
            status={contract.status} 
            onStageClick={(status) => {
              setPendingStage(status);
              setShowStageConfirmModal(true);
            }}
            className="w-full shrink-0" 
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 border-b border-[var(--t-border)] flex gap-0.5 shrink-0 bg-[var(--t-surface)] overflow-x-auto no-scrollbar">
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 py-1.5 px-2.5 text-[11px] font-semibold transition-colors border-b-2 relative top-[1px] whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-[var(--t-primary)] border-[var(--t-primary)]'
                  : 'text-[var(--t-text-muted)] border-transparent hover:text-[var(--t-text)]'
              }`}
            >
              <TabIcon size={12} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className={`flex-1 min-h-0 ${activeTab === 'editor' ? 'flex flex-col overflow-hidden p-1.5' : 'overflow-y-auto custom-scrollbar p-4'} bg-[var(--t-bg)]`}>
        {activeTab === 'editor' && (
          <ContractEditor
            initialContent={latestContent}
            initialLeadId={contract.lead_id}
            initialBuyerLeadId={contract.buyer_lead_id}
            initialSellerLeadId={contract.seller_lead_id}
            onSave={handleSave}
            isDraft={isDraft}
          />
        )}

        {activeTab === 'preview' && (
          <div className="max-w-4xl mx-auto">
            {latestContent ? (
              <ContractViewer content={latestContent} viewerRole="agent" />
            ) : (
              <div className="text-center py-16 text-[var(--t-text-muted)]">
                <FileText size={40} className="mx-auto mb-3 opacity-40" />
                <p>No content yet. Go to the Editor tab to add contract terms.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'signatures' && (
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold text-[var(--t-text)]">Signatures</h2>
              {contract.signing_token && (
                <div className="flex items-center gap-2 text-xs text-[var(--t-text-muted)]">
                  <span>Signing link expires:</span>
                  <span className="font-semibold text-[var(--t-text)]">
                    {contract.expires_at
                      ? format(new Date(contract.expires_at), 'MMM d, yyyy')
                      : 'Never'}
                  </span>
                </div>
              )}
            </div>

            {contract.signatures.length === 0 ? (
              <div className="text-center py-16 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl">
                <FileSignature size={36} className="mx-auto mb-3 text-[var(--t-text-muted)] opacity-50" />
                <p className="text-[var(--t-text-muted)] font-medium">No signatures yet</p>
                <p className="text-sm text-[var(--t-text-muted)] mt-1">
                  {isDraft ? 'Send the contract for signature first.' : 'Waiting for parties to sign.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contract.signatures.map(sig => (
                  <div key={sig.id} className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--t-success-dim)] flex items-center justify-center">
                        <CheckCircle2 size={16} className="text-[var(--t-success)]" />
                      </div>
                      <div>
                        <p className="font-bold text-[var(--t-text)] text-sm">{sig.signer_name}</p>
                        <p className="text-xs text-[var(--t-text-muted)] uppercase tracking-wide">{sig.signer_type}</p>
                      </div>
                    </div>
                    <div className="border border-[var(--t-border)] rounded-xl overflow-hidden bg-white mb-3">
                      <img src={sig.signature_data} alt="Signature" className="w-full max-h-20 object-contain p-2" />
                    </div>
                    <div className="space-y-1 text-xs text-[var(--t-text-muted)]">
                      <p>Signed: {format(new Date(sig.signed_at), 'MMM d, yyyy h:mm a')}</p>
                      {sig.ip_address && <p>IP: {sig.ip_address}</p>}
                      {sig.consent_given && (
                        <p className="text-[var(--t-success)] flex items-center gap-1">
                          <CheckCircle2 size={11} /> Electronic consent confirmed
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {contract.signing_token && (
              <div className="p-4 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl">
                <p className="text-xs font-semibold text-[var(--t-text-muted)] mb-2 uppercase tracking-wider">Signing Link</p>
                <div className="flex items-center gap-3">
                  <input
                    readOnly
                    value={`${window.location.origin}/sign/${contract.signing_token}`}
                    className="flex-1 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-lg px-3 py-2 text-xs text-[var(--t-text)] focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/sign/${contract.signing_token}`);
                      toast.success('Copied!');
                    }}
                    className="p-2 bg-[var(--t-surface-hover)] rounded-lg hover:bg-[var(--t-primary-dim)] hover:text-[var(--t-primary)] transition-colors text-[var(--t-text-muted)] shrink-0"
                  >
                    <Copy size={15} />
                  </button>
                </div>
              </div>
            )}

            {/* Invite Buyer/Seller Card */}
            <div className="p-6 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl space-y-4 mt-6">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[var(--t-primary)]">Invite Buyer/Seller</h3>
                <p className="text-xs text-[var(--t-text-muted)] mt-1">Generate a sign-up and auto-link invitation for the other party.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Full Name"
                  id="invite-name-input"
                  className="bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-3 py-2 text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  id="invite-email-input"
                  className="bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-3 py-2 text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
                />
                <select
                  id="invite-role-input"
                  className="bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-3 py-2 text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] appearance-none"
                >
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                </select>
              </div>

              <button
                type="button"
                onClick={async () => {
                  const nameInput = document.getElementById('invite-name-input') as HTMLInputElement;
                  const emailInput = document.getElementById('invite-email-input') as HTMLInputElement;
                  const roleInput = document.getElementById('invite-role-input') as HTMLSelectElement;

                  const name = nameInput?.value.trim();
                  const email = emailInput?.value.trim();
                  const role = roleInput?.value || 'buyer';

                  if (!name || !email) {
                    toast.error('Please enter a name and email');
                    return;
                  }

                  try {
                    // Create/update Lead in Supabase
                    const { data: leadData, error: leadError } = await supabase
                      .from('leads')
                      .insert({
                        name,
                        email,
                        status: 'contacted',
                        property_type: 'single-family',
                        team_id: contract.team_id || currentUser?.teamId,
                        notes: `Invited to sign contract "${contract.title}" as ${role}.`
                      })
                      .select()
                      .single();

                    if (leadError) throw leadError;

                    // Update contract with lead ID
                    const updates: any = {};
                    if (role === 'buyer') {
                      updates.buyer_lead_id = leadData.id;
                    } else {
                      updates.seller_lead_id = leadData.id;
                    }

                    // Append to activity log
                    const newLog = [
                      ...contract.activity_log,
                      {
                        id: uuidv4(),
                        note: `Invited ${name} (${role}) to sign contract`,
                        actor: currentUser?.name || 'Agent',
                        action: 'invited',
                        timestamp: new Date().toISOString()
                      }
                    ];
                    updates.activity_log = newLog;

                    const { error: contractUpdateError } = await supabase
                      .from('contracts')
                      .update(updates)
                      .eq('id', contract.id);

                    if (contractUpdateError) throw contractUpdateError;

                    // Generate link
                    const link = `${window.location.origin}/signup?email=${encodeURIComponent(email)}&role=${role}&contractId=${contract.id}&name=${encodeURIComponent(name)}&agentId=${currentUser?.id}`;
                    
                    navigator.clipboard.writeText(link);
                    toast.success('Invitation link copied to clipboard!');
                    
                    // Clear inputs
                    if (nameInput) nameInput.value = '';
                    if (emailInput) emailInput.value = '';

                    // Reload contract data to reflect update
                    fetchContract(contract.id);
                  } catch (err: any) {
                    console.error('Failed to generate invite:', err);
                    toast.error('Failed to generate invitation: ' + err.message);
                  }
                }}
                className="px-4 py-2 bg-[var(--t-primary)] text-white text-xs font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
              >
                Generate & Copy Invitation Link
              </button>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h2 className="text-base font-bold text-[var(--t-text)] flex items-center gap-2 mb-4">
                <Activity size={16} /> Activity Log
              </h2>
              <div className="space-y-2">
                {[...contract.activity_log].reverse().map((entry, idx) => (
                  <div key={entry.id || idx} className="flex items-start gap-3 p-3 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-[var(--t-primary)] mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--t-text)]">
                        {ACTION_LABEL[entry.action] || entry.action}
                      </p>
                      {entry.note && <p className="text-xs text-[var(--t-text-muted)]">{entry.note}</p>}
                      <p className="text-xs text-[var(--t-text-muted)] mt-1">
                        {entry.actor} · {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                    <span className="text-xs text-[var(--t-text-muted)] shrink-0">
                      {format(new Date(entry.timestamp), 'MMM d, h:mm a')}
                    </span>
                  </div>
                ))}
                {contract.activity_log.length === 0 && (
                  <p className="text-sm text-[var(--t-text-muted)] text-center py-6">No activity yet.</p>
                )}
              </div>
            </div>
            <VersionHistory versions={contract.versions} isCreator={true} />
          </div>
        )}

        {activeTab === 'certificate' && isExecuted && (
          <ContractCertificate contract={contract} />
        )}
      </div>

      {/* Send for Signature Modal */}
      <Modal isOpen={showSendModal} onClose={() => setShowSendModal(false)} title="Send for Signature" maxWidth="max-w-md">
        <div className="p-6">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 bg-[var(--t-success-dim)] rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 size={32} className="text-[var(--t-success)]" />
            </div>
            <h3 className="text-lg font-bold text-[var(--t-text)] mb-1">Ready to Sign!</h3>
            <p className="text-sm text-[var(--t-text-muted)]">Share this secure link with all signers. It expires in 14 days.</p>
          </div>
          <div className="bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl p-3 flex items-center gap-2 mb-5">
            <input readOnly value={signingLink} className="flex-1 bg-transparent text-sm text-[var(--t-text)] focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap min-w-0" />
            <button onClick={copyToClipboard} className="p-2 bg-[var(--t-surface-hover)] text-[var(--t-text)] hover:text-[var(--t-primary)] rounded-lg transition-colors shrink-0">
              <Copy size={15} />
            </button>
          </div>
          <div className="flex items-start gap-3 p-4 bg-[var(--t-info-dim)]/50 border border-[var(--t-info-dim)] rounded-xl">
            <AlertCircle size={16} className="text-[var(--t-info)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--t-text-muted)] leading-relaxed">
              Anyone with this link can view visible fields and sign. Only share with the intended signers.
            </p>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Contract" maxWidth="max-w-sm">
        <div className="p-6">
          {canDelete ? (
            <>
              <p className="text-[var(--t-text-muted)] text-sm mb-6">
                Are you sure you want to delete <span className="font-bold text-[var(--t-text)]">"{contract.title}"</span>? This cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)] hover:bg-[var(--t-surface)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 bg-[var(--t-error)] text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {isDeleting ? <Loader2 size={15} className="animate-spin mx-auto" /> : 'Delete'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-12 h-12 rounded-full bg-[var(--t-warning-dim)] flex items-center justify-center mb-3">
                  <AlertCircle size={22} className="text-[var(--t-warning)]" />
                </div>
                <h3 className="font-bold text-[var(--t-text)] mb-2">Cannot Delete Active Contract</h3>
                <p className="text-sm text-[var(--t-text-muted)]">
                  Only contracts in <strong>Draft</strong> or <strong>Rejected</strong> status can be deleted.
                  This contract is currently <strong>{contract.status}</strong>.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="w-full mt-4 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)] transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </Modal>

      {/* Save as Template Modal */}
      <Modal isOpen={showTemplateModal} onClose={() => setShowTemplateModal(false)} title="Save as Template" maxWidth="max-w-sm">
        <div className="p-6 space-y-4">
          <p className="text-sm text-[var(--t-text-muted)]">Save this contract's fields and terms as a reusable template.</p>
          <div>
            <label className="block text-xs font-semibold text-[var(--t-text-muted)] mb-1.5 uppercase tracking-wider">Template Name</label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. My Wholesale Agreement"
              className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--t-text-muted)] mb-1.5 uppercase tracking-wider">Category</label>
            <input
              type="text"
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
              placeholder="e.g. Acquisitions, Custom"
              className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button onClick={() => setShowTemplateModal(false)} className="flex-1 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)]">
              Cancel
            </button>
            <button
              onClick={handleSaveAsTemplate}
              disabled={!templateName.trim()}
              className="flex-1 px-4 py-2.5 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50"
            >
              Save Template
            </button>
          </div>
        </div>
      </Modal>

      {/* Move Stage Confirmation Modal */}
      <Modal isOpen={showStageConfirmModal} onClose={() => { setShowStageConfirmModal(false); setPendingStage(null); }} title="Confirm Stage Change" maxWidth="max-w-sm">
        <div className="p-6">
          <p className="text-[var(--t-text-muted)] text-sm mb-6">
            Are you sure you want to move this contract back to the <span className="font-bold text-[var(--t-text)]">"{pendingStage}"</span> stage? This will reset status flags and log the activity.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowStageConfirmModal(false); setPendingStage(null); }}
              className="flex-1 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)] hover:bg-[var(--t-surface)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (pendingStage) {
                  await handleStatusChange(pendingStage);
                }
                setShowStageConfirmModal(false);
                setPendingStage(null);
              }}
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-60"
            >
              {isSaving ? <Loader2 size={15} className="animate-spin mx-auto" /> : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
